import React from "react";
import { InlineMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) [x, y] = [y, x % y];
  return x || 1;
}

function normalizeFraction(numerator, denominator) {
  if (denominator < 0) return normalizeFraction(-numerator, -denominator);
  if (numerator === 0) return { numerator: 0, denominator: 1 };
  const divisor = gcd(numerator, denominator);
  return { numerator: numerator / divisor, denominator: denominator / divisor };
}

function improperToMixed(numerator, denominator) {
  const sign = numerator < 0 ? -1 : 1;
  const absNumerator = Math.abs(numerator);
  const whole = Math.floor(absNumerator / denominator) * sign;
  const rest = absNumerator % denominator;
  const fraction = normalizeFraction(rest, denominator);
  return { whole, numerator: fraction.numerator, denominator: fraction.denominator };
}

function mixedToImproper({ whole, numerator, denominator }) {
  const sign = whole < 0 ? -1 : 1;
  return normalizeFraction(sign * (Math.abs(whole) * denominator + numerator), denominator);
}

function fractionLatex(numerator, denominator) {
  if (denominator === 1) return `${numerator}`;
  if (numerator < 0) return `-\\frac{${Math.abs(numerator)}}{${denominator}}`;
  return `\\frac{${numerator}}{${denominator}}`;
}

function mixedLatex({ whole, numerator, denominator }) {
  if (!numerator) return `${whole}`;
  const sign = whole < 0 ? "-" : "";
  return `${sign}${Math.abs(whole)}\\frac{${numerator}}{${denominator}}`;
}

function MathText({ math, size = "1.35rem" }) {
  return (
    <span style={{ fontSize: size, lineHeight: 1.15 }}>
      <InlineMath math={math} />
    </span>
  );
}

function parseMixedOrFraction(raw) {
  if (typeof raw !== "string") return null;
  const cleaned = raw.trim().replace(",", ".").replace(/[−–]/g, "-").replace(/\s+/g, " ");
  const mixed = cleaned.match(/^(-?\d+)\s+(\d+)\/(\d+)$/);
  if (mixed) {
    const whole = Number(mixed[1]);
    const numerator = Number(mixed[2]);
    const denominator = Number(mixed[3]);
    if (!Number.isInteger(whole) || !Number.isInteger(numerator) || !Number.isInteger(denominator)) return null;
    if (denominator === 0 || numerator <= 0 || numerator >= denominator) return null;
    return {
      kind: "mixed",
      whole,
      numerator,
      denominator,
      fraction: mixedToImproper({ whole, numerator, denominator }),
      simplifiedFractionPart: gcd(numerator, denominator) === 1,
    };
  }
  const fraction = cleaned.replace(/\s+/g, "").match(/^(-?\d+)\/(-?\d+)$/);
  if (fraction) {
    const rawNumerator = Number(fraction[1]);
    const rawDenominator = Number(fraction[2]);
    if (!Number.isInteger(rawNumerator) || !Number.isInteger(rawDenominator) || rawDenominator === 0) return null;
    return {
      kind: "fraction",
      rawNumerator,
      rawDenominator,
      fraction: normalizeFraction(rawNumerator, rawDenominator),
      simplified: gcd(rawNumerator, rawDenominator) === 1,
    };
  }
  const integer = cleaned.match(/^-?\d+$/);
  if (integer) {
    return {
      kind: "integer",
      rawNumerator: Number(cleaned),
      rawDenominator: 1,
      fraction: { numerator: Number(cleaned), denominator: 1 },
      simplified: true,
    };
  }
  return null;
}

function warning(message) {
  return {
    isCorrect: false,
    score: 0,
    maxScore: 1,
    messageType: "warning",
    message,
  };
}

function makeImproperToMixedTask(type, options = {}) {
  const denominators = options.denominators || [3, 4, 5, 6, 7, 8, 9];
  const validDenominators = denominators.filter((denominator) => {
    const candidates = Array.from({ length: denominator - 1 }, (_, index) => index + 1).filter((n) =>
      options.reducibleRest ? gcd(n, denominator) > 1 : gcd(n, denominator) === 1
    );
    return candidates.length > 0;
  });
  const denominator = pick(validDenominators);
  const whole = randInt(options.minWhole || 1, options.maxWhole || 4);
  const candidates = Array.from({ length: denominator - 1 }, (_, index) => index + 1).filter((n) =>
    options.reducibleRest ? gcd(n, denominator) > 1 && n % denominator !== 0 : gcd(n, denominator) === 1
  );
  const rest = pick(candidates);
  const sign = options.negative ? -1 : 1;
  const numerator = sign * (whole * denominator + rest);
  const improper = { numerator, denominator };
  return {
    type,
    data: {
      improper,
      mixed: improperToMixed(numerator, denominator),
      requireSimplifyInstruction: Boolean(options.requireSimplifyInstruction || options.reducibleRest),
    },
  };
}

function makeMixedToImproperTask(type, options = {}) {
  const denominators = options.denominators || [3, 4, 5, 6, 7, 8, 9];
  const validDenominators = denominators.filter((denominator) => {
    const candidates = Array.from({ length: denominator - 1 }, (_, index) => index + 1).filter((n) =>
      options.reducibleRest ? gcd(n, denominator) > 1 : gcd(n, denominator) === 1
    );
    return candidates.length > 0;
  });
  const denominator = pick(validDenominators);
  const wholeMagnitude = randInt(options.minWhole || 1, options.maxWhole || 4);
  const candidates = Array.from({ length: denominator - 1 }, (_, index) => index + 1).filter((n) =>
    options.reducibleRest ? gcd(n, denominator) > 1 && n % denominator !== 0 : gcd(n, denominator) === 1
  );
  const rest = pick(candidates);
  const mixed = { whole: options.negative ? -wholeMagnitude : wholeMagnitude, numerator: rest, denominator };
  return {
    type,
    data: {
      mixed: improperToMixed(mixedToImproper(mixed).numerator, mixedToImproper(mixed).denominator),
      improper: mixedToImproper(mixed),
      rawMixed: mixed,
      requireSimplifyInstruction: Boolean(options.requireSimplifyInstruction || options.reducibleRest),
    },
  };
}

function smallImproperTask() {
  return makeImproperToMixedTask("ImproperToMixedTask", { denominators: [3, 4, 5, 6], maxWhole: 3 });
}

function mixedToImproperTask() {
  return makeMixedToImproperTask("MixedToImproperTask", { denominators: [3, 4, 5, 6, 7], maxWhole: 7 });
}

function reducibleImproperToMixedTask() {
  return makeImproperToMixedTask("ReducibleImproperToMixedTask", {
    denominators: [4, 6, 8, 9, 10, 12, 14],
    reducibleRest: true,
    maxWhole: 7,
    requireSimplifyInstruction: true,
  });
}

function reducibleMixedToImproperTask() {
  return makeMixedToImproperTask("ReducibleMixedToImproperTask", {
    denominators: [4, 6, 8, 9, 10, 12, 14],
    reducibleRest: true,
    maxWhole: 7,
    requireSimplifyInstruction: true,
  });
}

function negativeImproperToMixedTask() {
  return makeImproperToMixedTask("NegativeImproperToMixedTask", {
    denominators: [3, 4, 5, 6, 8, 10, 12, 14],
    reducibleRest: Math.random() < 0.5,
    maxWhole: 7,
    negative: true,
    requireSimplifyInstruction: true,
  });
}

function negativeMixedToImproperTask() {
  return makeMixedToImproperTask("NegativeMixedToImproperTask", {
    denominators: [3, 4, 5, 6, 8, 10, 12, 14],
    reducibleRest: Math.random() < 0.5,
    maxWhole: 7,
    negative: true,
    requireSimplifyInstruction: true,
  });
}

export function generateTasks() {
  return [
    smallImproperTask(),
    mixedToImproperTask(),
    reducibleImproperToMixedTask(),
    reducibleMixedToImproperTask(),
    negativeImproperToMixedTask(),
    negativeMixedToImproperTask(),
  ];
}

function isMixedToImproper(type) {
  return (
    type === "MixedToImproperTask" ||
    type === "ReducibleMixedToImproperTask" ||
    type === "NegativeMixedToImproperTask"
  );
}

function isImproperToMixed(type) {
  return (
    type === "ImproperToMixedTask" ||
    type === "ReducibleImproperToMixedTask" ||
    type === "NegativeImproperToMixedTask"
  );
}

export function renderTask({ type, data }) {
  if (isMixedToImproper(type)) {
    return (
      <p>
        Zamień liczbę mieszaną <MathText math={mixedLatex(data.rawMixed || data.mixed)} /> na ułamek niewłaściwy
        {data.requireSimplifyInstruction ? " i skróć wynik, jeśli się da" : ""}.
      </p>
    );
  }

  return (
    <p>
      Zamień ułamek niewłaściwy <MathText math={fractionLatex(data.improper.numerator, data.improper.denominator)} />{" "}
      na liczbę mieszaną{data.requireSimplifyInstruction ? " i skróć część ułamkową, jeśli się da" : ""}.
    </p>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label="wynik"
      placeholder={isImproperToMixed(task.type) ? "np. 2 1/3" : "np. 7/3"}
    />
  );
}

export function checkAnswer({ type, data }, userAnswer) {
  const parsed = parseMixedOrFraction(userAnswer?.raw);
  if (!parsed) return false;

  if (isMixedToImproper(type)) {
    if (parsed.kind !== "fraction" && parsed.kind !== "integer") return false;
    const sameValue =
      parsed.fraction.numerator === data.improper.numerator && parsed.fraction.denominator === data.improper.denominator;
    if (!sameValue) return false;
    if (!parsed.simplified) {
      return warning(
        `Wartość ułamka jest poprawna, ale polecenie wymaga postaci skróconej: $${fractionLatex(
          data.improper.numerator,
          data.improper.denominator
        )}$.`
      );
    }
    return true;
  }

  if (parsed.kind !== "mixed") return false;
  const sameValue =
    parsed.fraction.numerator === data.improper.numerator && parsed.fraction.denominator === data.improper.denominator;
  if (!sameValue) return false;
  if (!parsed.simplifiedFractionPart) {
    return warning(
      `Wartość liczby mieszanej jest poprawna, ale część ułamkową trzeba skrócić: $${mixedLatex(data.mixed)}$.`
    );
  }
  return parsed.whole === data.mixed.whole && parsed.numerator === data.mixed.numerator && parsed.denominator === data.mixed.denominator;
}

export function renderAnswer({ type, data }) {
  if (isMixedToImproper(type)) {
    return <MathText math={fractionLatex(data.improper.numerator, data.improper.denominator)} />;
  }
  return <MathText math={mixedLatex(data.mixed)} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) return <i>(brak odpowiedzi)</i>;
  return <MathText math={formatLatexPreview(userAnswer.raw)} />;
}

export function renderExplanation({ type, data }) {
  if (isMixedToImproper(type)) {
    const mixed = data.rawMixed || data.mixed;
    const sign = mixed.whole < 0 ? "-" : "";
    const unsimplifiedNumerator = Math.abs(mixed.whole) * mixed.denominator + mixed.numerator;
    return (
      <div>
        <p>
          Całości zamieniamy na części o mianowniku <InlineMath math={`${mixed.denominator}`} />.
        </p>
        <p>
          <MathText
            math={`${mixedLatex(mixed)}=${sign}\\frac{${Math.abs(mixed.whole)}\\cdot ${mixed.denominator}+${mixed.numerator}}{${mixed.denominator}}=${fractionLatex(
              sign ? -unsimplifiedNumerator : unsimplifiedNumerator,
              mixed.denominator
            )}`}
          />
        </p>
        {data.improper.numerator !== (sign ? -unsimplifiedNumerator : unsimplifiedNumerator) ||
        data.improper.denominator !== mixed.denominator ? (
          <p>
            Po skróceniu otrzymujemy <MathText math={fractionLatex(data.improper.numerator, data.improper.denominator)} />.
          </p>
        ) : null}
      </div>
    );
  }

  const sign = data.improper.numerator < 0 ? "-" : "";
  const absNumerator = Math.abs(data.improper.numerator);
  const whole = Math.floor(absNumerator / data.improper.denominator);
  const rest = absNumerator % data.improper.denominator;
  return (
    <div>
      <p>
        Dzielimy licznik przez mianownik: <InlineMath math={`${absNumerator}:${data.improper.denominator}`} />.
        Otrzymujemy <InlineMath math={`${whole}`} /> całości i resztę <InlineMath math={`${rest}`} />.
      </p>
      <p>
        <MathText
          math={`${fractionLatex(data.improper.numerator, data.improper.denominator)}=${sign}${whole}+${sign}${fractionLatex(
            rest,
            data.improper.denominator
          )}=${mixedLatex(data.mixed)}`}
        />
      </p>
      {data.mixed.denominator !== data.improper.denominator && (
        <p>
          Część ułamkową skracamy, więc końcowy zapis to <MathText math={mixedLatex(data.mixed)} />.
        </p>
      )}
    </div>
  );
}

const taskAdapter = {
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
};

export const registry = {
  ImproperToMixedTask: taskAdapter,
  MixedToImproperTask: taskAdapter,
  ReducibleImproperToMixedTask: taskAdapter,
  ReducibleMixedToImproperTask: taskAdapter,
  NegativeImproperToMixedTask: taskAdapter,
  NegativeMixedToImproperTask: taskAdapter,
};
